import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useDebouncedCallback } from '../hooks/useDebouncedCallback';
import { setterGantScroll } from '../store/slices/gantScrollSlice';

type ScrollContextType = {
    scrollX: number;
    setScrollX: (value: number) => void;
    isScrollingHeader: boolean;
};

const SCROLLX_DISPATCH_DELAY = 150;
const HEADER_UPDATE_DELAY = 1000;

const ScrollContext = createContext<ScrollContextType | undefined>(undefined);

export const ScrollProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [scrollX, setScrollX] = useState<number>(0);
    const [isScrollingHeader, setIsScrollingHeader] = useState(true);
    const dispatch = useDispatch();

    const currentScrollXDispatch = useDebouncedCallback((currentScrollX: number) => {
        dispatch(setterGantScroll({ currentScrollX }));
    }, SCROLLX_DISPATCH_DELAY);

    const debouncedHeaderUpdate = useDebouncedCallback(() => {
        setIsScrollingHeader(false);
    }, HEADER_UPDATE_DELAY);

    useEffect(() => {
        setIsScrollingHeader(true);
        debouncedHeaderUpdate();
        currentScrollXDispatch(scrollX);
    }, [scrollX]);

    const contextValue = useMemo(
        () => ({
            scrollX,
            setScrollX,
            isScrollingHeader,
        }),
        [scrollX, scrollY, isScrollingHeader],
    );

    return <ScrollContext.Provider value={contextValue}>{children}</ScrollContext.Provider>;
};

export const useScrollContext = () => {
    const context = useContext(ScrollContext);
    if (!context) {
        throw new Error('useScrollContext must be used within a ScrollProvider');
    }
    return context;
};
